import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import * as answer from '../../lib/answer';
import {withUser} from '../../lib/user';
import SideModal from '../../components/SideModal';
import Notification from '../../components/Notification';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Answer from '../../components/Answer';
import Form from '../../components/Form';
import AnswerInput from '../../components/AnswerInput';
import TextArea from '../../components/TextArea';

import {calculateAlerts} from './shared';
import Role from "../../lib/Role";

class AnswersTab extends PureComponent {
  handleAnswersChanged = (question, answers) => {
    const {sheet, row, onAnswersChange} = this.props;
    onAnswersChange(sheet, row, question, answers);
  }

  render() {
    const {template, catalog, sheet, row} = this.props;
    const alerts = calculateAlerts(template, row);
    const questions = [...template.contextQuestions, ...template.questions].sort((a, b) => a.order - b.order);
    
    const answersByQuestionId = row.answers.reduce((map, answer) => {
      if (!map[answer.questionId]) {
        map[answer.questionId] = [];
      }

      map[answer.questionId].push(answer);
      return map;
    }, sheet.contextAnswers.reduce((map, answer) => {
      if (!map[answer.questionId]) {
        map[answer.questionId] = [];
      }

      map[answer.questionId].push(answer);
      return map;
    }, {}));

    return (
      <SideModal.Content>
        {alerts.length > 0 && (
          <NotificationContainer>
            <Notification warning>
              {alerts.length === 1 ? alerts[0].name : (
                <ul>
                  {alerts.map(alert => (
                    <li key={alert.id}>{alert.name}</li>
                  ))}
                </ul>
              )}
            </Notification>
          </NotificationContainer>
        )}
        <AnswerList>
          {questions.map((question, i) => (
            <AnswerEditor
              key={question.id}
              index={i + 1}
              catalog={catalog}
              sheet={sheet}
              row={row}
              question={question}
              answers={answersByQuestionId[question.id] || []}
              onChange={this.handleAnswersChanged}
            />
          ))}
        </AnswerList>
      </SideModal.Content>
    );
  }
}

class AnswerEditor extends PureComponent {
  state = {
    editing: false,
    saving: false,
    validate: false,
    isValidAnswer: true,
    auditComment: '',
    values: this.props.answers.map(answer => answer.value)
  };

  toggleEditing = () => {
    this.setState(prevState => ({
      editing: !prevState.editing,
      values: this.props.answers.map(answer => answer.value),
      auditComment: ''
    }));
  }

  handleUpdateAnswers = (values, isValidAnswer) => {
    this.setState({values, isValidAnswer});
  }

  handleClearAnswers = async () => {
    this.setState({values: [], isValidAnswer: true});
  }

  handleSaveAnswers = async () => {
    const {sheet, row, question, credentials, onChange} = this.props;
    const {isValidAnswer, values, auditComment} = this.state;

    // Make sure the answer is valid.
    if (!isValidAnswer) {
      this.setState({validate: true});
      return;
    }

    const now = new Date().getTime();
    const answers = values
      .filter(value => !!value)
      .map((value, index) => answer.create(question.id, value, new Date(now + index)));

    try {
      this.setState({saving: true});
      await api.editAnswers(sheet.id, question.context ? null : row.id, question.id, answers, auditComment, credentials);
      onChange(question, answers);
      this.setState({editing: false, validate: false, auditComment: ''});
    }
    catch (e) {
      NotificationManager.error('Your answers could not be saved! Please try again.');
    }
    finally {
      this.setState({saving: false});
    }
  }

  handleChangeAuditComment = e => {
    this.setState({auditComment: e.target.value});
  }

  render() {
    const {user, catalog, index, question, answers} = this.props;
    const {editing, saving, validate, isValidAnswer, values, auditComment} = this.state;

    return (
      <AnswerListItem key={question.id}>
        <Form onSubmit={this.handleSaveAnswers}>
          <AnswerListItemHeader>
            <h4><span>{index}.</span> {question.text}</h4>
            {canEditRow(question, user) && (
              editing ? (
                <>
                  {values && values.length > 0 && (<Button tertiary disabled={saving} onClick={this.handleClearAnswers}>Clear</Button>)}
                  <Button tertiary onClick={this.toggleEditing}>Cancel</Button>
                  <Button type="submit" disabled={saving || (validate && !isValidAnswer)}>Save</Button>
                </>
              ) : (
                <Button tertiary onClick={this.toggleEditing}>Edit</Button>
              )
            )}
          </AnswerListItemHeader>
          <AnswerListItemContent>
            {editing ? (
              <>
                <Form.Group>
                  <AnswerInput
                    label="Answer"
                    invalid={validate}
                    question={question}
                    catalog={catalog}
                    values={values}
                    onChange={this.handleUpdateAnswers}
                  />
                </Form.Group>
                <Form.Group>
                  <TextArea
                    id={`audit-comment-${question.id}`}
                    label="Reason for Change"
                    placeholder="Add a comment explaining why you're editing this answer"
                    value={auditComment}
                    onChange={this.handleChangeAuditComment}
                  />
                </Form.Group>
              </>
            ) : (
              <Answer question={question} answers={answers} catalog={catalog} />
            )}
          </AnswerListItemContent>
        </Form>
      </AnswerListItem>
    );
  }
}

AnswerEditor = withUser(AnswerEditor);

function canEditRow(question, user) {
  return user.role === Role.ADMIN && question.type !== 'SIGNATURE';
}

const NotificationContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AnswerListItem = styled(Card)`
  margin-bottom: 1.5rem;

  :last-child {
    margin-bottom: 0;
  }
`;

const AnswerListItemHeader = styled.div`
  display: flex;
  padding: 1rem 1rem 0.5rem 2rem;

  > * {
    margin-left: 0.5rem;
  }

  h4 {
    margin: 0;
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 1.125rem;
    line-height: 1.3;
    font-weight: 500;

    span {
      min-width: 2rem;
    }
  }
`;

const AnswerListItemContent = styled.div`
  line-height: 1.5;
  padding: 0.5rem 1rem 1.5rem 4rem;
`;

export default AnswersTab;